import React, { useState, lazy, Suspense } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Modal,
  IconButton,
  Fade,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { ArrowBack, Close } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// Lazy load your component files
const GovernancePage = lazy(() => import("./GovernancePage"));
const DigitalPage = lazy(() => import("./DigitalPage"));
const AgriculturePage = lazy(() => import("./AgriculturePage"));
const IndustryPage = lazy(() => import("./IndustryPage"));
const TransportPage = lazy(() => import("./TransportPage"));
const WastePage = lazy(() => import("./WastePage"));
const ForestryPage = lazy(() => import("./ForestryPage"));
const WaterPage = lazy(() => import("./WaterPage"));
const EngagementPage = lazy(() => import("./EngagementPage"));
const ResearchPage = lazy(() => import("./ResearchPage"));

// Animations
const growFromCenter = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.3) translate(-50%, -50%);
  }
  50% {
    opacity: 1;
    transform: scale(1.1) translate(-50%, -50%);
  }
  100% {
    opacity: 1;
    transform: scale(1) translate(var(--final-x), var(--final-y));
  }
`;

const slideVideoUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const shrinkImage = keyframes`
  from {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(1);
    width: 60%;
    height: 60%;
  }
  to {
    top: 0%;
    left: 50%;
    transform: translate(-50%, 0) scale(0.4);
    width: 40%;
    height: 30%;
  }
`;

const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
  }
  50% {
    box-shadow: 0 0 40px rgba(255, 255, 255, 0.8), 0 0 60px rgba(45, 134, 89, 0.6);
  }
  100% {
    boxShadow: 0 0 20px rgba(255, 255, 255, 0.5);
  }
`;

// Styled Components
const PageWrapper = styled(Box)({
  minHeight: "100vh",
  backgroundImage: "url('images/epa2.jpg')",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  position: "relative",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
  },
});

const CenteredImage = styled(Box)({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "60%",
  height: "60%",
  backgroundImage: "url('images/EPALogo.png')",
  backgroundSize: "contain",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  backgroundColor: "#faebd7",
  cursor: "pointer",
  borderRadius: "25px",
  boxShadow:
    "0 25px 50px rgba(45, 134, 89, 0.3), 0 0 0 15px rgba(255, 255, 255, 0.1)",
  zIndex: 10,
  transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translate(-50%, -50%) scale(1.05)",
    boxShadow:
      "0 30px 60px rgba(45, 134, 89, 0.4), 0 0 0 15px rgba(255, 255, 255, 0.2)",
    animation: `${pulseGlow} 2s ease-in-out infinite`,
  },
  "&.moved-up": {
    animation: `${shrinkImage} 1s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
  },
});

const VideoContainer = styled(Box)({
  position: "absolute",
  top: "25%",
  left: "30%",
  transform: "translate(-50%, -50%)",
  width: "40%",
  borderRadius: "16px",
  overflow: "hidden",
  boxShadow: "0 20px 40px rgba(45, 134, 89, 0.3)",
  backgroundColor: "#000",
  opacity: 0,
  zIndex: 5,
  "&.visible": {
    animation: `${slideVideoUp} 1s cubic-bezier(0.4, 0, 0.2, 1) forwards 0.3s`,
  },
});

const VideoWrapper = styled(Box)({
  position: "relative",
  width: "100%",
  paddingBottom: "56.25%",
  backgroundColor: "#000",
});

const VideoElement = styled("video")({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  display: "block",
  backgroundColor: "#000",
});

const VideoTitle = styled(Typography)({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  padding: "12px 16px",
  background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
  color: "white",
  fontSize: "0.95rem",
  fontWeight: 600,
  zIndex: 5,
});

const SectionsContainer = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 6,
  opacity: 0,
  pointerEvents: "none",
  "&.visible": {
    opacity: 1,
    transition: "opacity 0.5s ease 1s",
  },
  "& > *": {
    pointerEvents: "auto", // All direct children can receive events
  },
});

const CategoryCard = styled(Card)(({ index }) => {
  const positions = {
    0: { x: "-400%", y: "-250%", delay: 1.2 },
    1: { x: "-400%", y: "-90%", delay: 1.4 },
    2: { x: "-400%", y: "80%", delay: 1.6 },
    3: { x: "300%", y: "-250%", delay: 3.0 },
    4: { x: "300%", y: "-90%", delay: 2.8 },
    5: { x: "300%", y: "80%", delay: 2.6 },
    6: { x: "-400%", y: "250%", delay: 1.8 },
    7: { x: "-170%", y: "250%", delay: 2.0 },
    8: { x: "70%", y: "250%", delay: 2.2 },
    9: { x: "300%", y: "250%", delay: 2.4 },
  };

  const pos = positions[index] || positions[0];

  return {
    position: "absolute",
    top: "50%",
    left: "50%",
    background: "linear-gradient(135deg, #000 0%, #003193 50%, #000 100%)",
    backdropFilter: "blur(15px)",
    border: "2px solid rgba(45, 134, 89, 0.5)",
    borderRadius: "15px",
    cursor: "pointer",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    width: "180px",
    height: "110px",
    minHeight: "110px",
    opacity: 0,
    transform: "scale(0.3) translate(-50%, -50%)",
    animation: `${growFromCenter} 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards ${pos.delay}s`,
    "--final-x": pos.x,
    "--final-y": pos.y,

    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(90deg, transparent, rgba(45, 134, 89, 0.3), transparent)",
      transition: "left 0.6s ease",
      borderRadius: "15px",
    },

    "&:hover": {
      transform: `translate(calc(-50% + ${pos.x}), calc(-50% + ${pos.y})) scale(1.1)`,
      background: "rgba(45, 134, 89, 0.25)",
      border: "2px solid rgba(45, 134, 89, 0.8)",
      boxShadow:
        "0 20px 40px rgba(45, 134, 89, 0.4), 0 0 20px rgba(45, 134, 89, 0.3)",
      "&::before": {
        left: "100%",
      },
    },
  };
});

const IconBox = styled(Box)({
  fontSize: "1.8rem",
  marginBottom: "6px",
  transition: "transform 0.3s ease",
  ".MuiCard-root:hover &": {
    transform: "scale(1.2)",
  },
});

const BackButton = styled(Button)({
  position: "absolute",
  top: "20px",
  left: "20px",
  zIndex: 1000,
  color: "white",
  border: "2px solid rgba(255, 255, 255, 0.4)",
  padding: "10px 24px",
  borderRadius: "25px",
  fontSize: "0.95rem",
  cursor: "pointer",
  background: "rgba(15, 23, 42, 0.8)",
  backdropFilter: "blur(15px)",
  boxShadow: "0 8px 25px rgba(45, 134, 89, 0.3)",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  fontWeight: 600,
  "&:hover": {
    borderColor: "rgba(255, 255, 255, 0.8)",
    transform: "translateY(-3px)",
    boxShadow: "0 12px 35px rgba(45, 134, 89, 0.5)",
    background: "rgba(45, 134, 89, 0.3)",
  },
});

const ModalContent = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "95%",
  maxWidth: "1400px",
  maxHeight: "90vh",
  backgroundColor: "transparent",
  borderRadius: "0px",
  padding: "0",
  boxShadow: "none",
  overflow: "hidden",
  outline: "none",
  border: "none",
}));

const LoadingContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "400px",
  background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
  borderRadius: "20px",
});

const MainMenu = () => {
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const categories = [
    {
      id: "GovernancePage",
      title: "Environmental Governance",
      icon: "🏛️",
      description: "Foundation for Climate Action",
    },
    {
      id: "DigitalPage",
      title: "Digital Transformation",
      icon: "💻",
      description: "Technology-Enabled Enforcement",
    },
    {
      id: "IndustryPage",
      title: "Industry",
      icon: "🏭",
      description: "Cleaner Green Energy",
    },
    {
      id: "AgriculturePage",
      title: "Agriculture & Livestock",
      icon: "🌾",
      description: "Smart Farming",
    },
    {
      id: "TransportPage",
      title: "Transport & Mobility",
      icon: "🚗",
      description: "Clean & Efficient Mobility",
    },
    {
      id: "WastePage",
      title: "Solid Waste & Plastic Management",
      icon: "🗑️",
      description: "Pollution Control to Economy",
    },
    {
      id: "ForestryPage",
      title: "Forestry & Carbon Credits",
      icon: "🌳",
      description: "Nature into Climate Capital",
    },
    {
      id: "WaterPage",
      title: "Water Resources & Climate Resilience",
      icon: "💧",
      description: "Protecting Water",
    },
    {
      id: "EngagementPage",
      title: "Public Engagement & Awareness",
      icon: "📣",
      description: "From Policy to People",
    },
    {
      id: "ResearchPage",
      title: "Research & Innovation",
      icon: "🎓",
      description: "Knowledge-Based Leadership",
    },
  ];

  const handleImageClick = () => {
    setShowContent(true);
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedCategory(null);
  };

  const handleBackClick = () => {
    if (showContent) {
      setShowContent(false);
    } else {
      navigate("/");
    }
  };

  // Render the appropriate component based on selected category
  const renderModalContent = () => {
    if (!selectedCategory) return null;

    const categoryComponents = {
      GovernancePage: GovernancePage,
      DigitalPage: DigitalPage,
      IndustryPage: IndustryPage,
      AgriculturePage: AgriculturePage,
      TransportPage: TransportPage,
      WastePage: WastePage,
      ForestryPage: ForestryPage,
      WaterPage: WaterPage,
      EngagementPage: EngagementPage,
      ResearchPage: ResearchPage,
    };

    const Component = categoryComponents[selectedCategory.id];

    if (Component) {
      return (
        <Suspense
          fallback={
            <LoadingContainer>
              <CircularProgress size={60} sx={{ color: "#2d8659" }} />
              <Typography variant="h6" sx={{ ml: 2, color: "#2d8659" }}>
                Loading {selectedCategory.title}...
              </Typography>
            </LoadingContainer>
          }
        >
          <Component />
        </Suspense>
      );
    }

    // Fallback for categories without specific components
    return (
      <Box
        sx={{
          background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
          borderRadius: "20px",
          padding: 4,
          minHeight: "400px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography variant="h4" sx={{ color: "#2d8659", mb: 2 }}>
          {selectedCategory.title}
        </Typography>
        <Typography variant="h6" sx={{ color: "#666", mb: 3 }}>
          {selectedCategory.description}
        </Typography>
        <Typography variant="body1" sx={{ color: "#555", textAlign: "center" }}>
          Component for {selectedCategory.title} is under development.
        </Typography>
      </Box>
    );
  };

  return (
    <PageWrapper>
      <BackButton startIcon={<ArrowBack />} onClick={handleBackClick}>
        Back
      </BackButton>

      {/* Centered Main Image with white background */}
      <CenteredImage
        className={showContent ? "moved-up" : ""}
        onClick={handleImageClick}
      />

      {/* Video Container */}
      {showContent && (
        <VideoContainer className="visible">
          <VideoWrapper>
            <VideoElement
              controls={true}
              src="videos/epa.mp4"
              preload="metadata"
              autoPlay
              // muted
            />
            <VideoTitle>Environmental Protection Agency - Overview</VideoTitle>
          </VideoWrapper>
        </VideoContainer>
      )}

      {/* Category Sections - Animated from center */}
      {showContent && (
        <SectionsContainer className="visible">
          {categories.map((category, index) => (
            <CategoryCard
              key={category.id}
              index={index}
              onClick={() => handleCategoryClick(category)}
            >
              <CardContent
                sx={{
                  textAlign: "center",
                  padding: "16px 8px !important",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <IconBox>{category.icon}</IconBox>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    // marginBottom: "4px",
                    fontSize: "1rem",
                    lineHeight: 1.1,
                  }}
                >
                  {category.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: "0.8rem",
                    lineHeight: 1.2,
                  }}
                >
                  {category.description}
                </Typography>
              </CardContent>
            </CategoryCard>
          ))}
        </SectionsContainer>
      )}

      {/* Modal for Category Content */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            sx: {
              // zIndex: 1200,
              backgroundColor: "rgba(0, 0, 0, 0.9)",
            },
          },
        }}
        closeAfterTransition
      >
        <Fade in={openModal} timeout={600}>
          <ModalContent>
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: "absolute",
                top: "20px",
                right: "20px",
                zIndex: 1300,
                color: "white",
                background: "rgba(0, 0, 0, 0.5)",
                backdropFilter: "blur(10px)",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  transform: "rotate(90deg) scale(1.1)",
                  boxShadow: "0 4px 15px rgba(255, 255, 255, 0.3)",
                },
              }}
            >
              <Close />
            </IconButton>

            {renderModalContent()}
          </ModalContent>
        </Fade>
      </Modal>
    </PageWrapper>
  );
};

export default MainMenu;
